import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { is } from "ramda";
import { type CSSProperties, type ForwardedRef, type ReactElement, type ReactNode, useMemo, useRef } from "react";
import { type AriaComboBoxProps, mergeProps, useComboBox, useFilter, useFocusRing, VisuallyHidden } from "react-aria";
import { useComboBoxState } from "react-stately";

import { useDOMRef, useResizeObserver } from "../../hooks.ts";
import formStyle from "../../styles/form.module.css";
import { forwardRef } from "../../utils/jsx/forwardRef.ts";
import { Badge } from "../Badge/Badge.tsx";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { Label } from "../Label/Label.tsx";
import { ListBox } from "../List/ListBox.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import style from "../TextField/TextField.module.css";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";

export interface ComboBoxProps<T> extends AriaComboBoxProps<T> {
  style?: CSSProperties;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  width?: CSSProperties["minWidth"];
  isLoading?: boolean;
  action?: ReactElement;
  extraAction?: ReactElement;
}

const ComboBoxRender = <T extends object>(props: ComboBoxProps<T>, ref: ForwardedRef<HTMLInputElement>): ReactNode => {
  // Use for Popover position
  const boxRef = useRef<HTMLDivElement>(null);

  const buttonRef = useRef(null);
  const inputRef = useDOMRef(ref);
  const listBoxRef = useRef(null);
  const popoverRef = useRef(null);

  // ComboBox
  const { contains } = useFilter({ sensitivity: "base" });
  const state = useComboBoxState<T>({
    defaultFilter: contains,
    ...props,
  });
  const { buttonProps, inputProps, listBoxProps, labelProps, descriptionProps, errorMessageProps } = useComboBox<T>(
    {
      ...props,
      label: props.label ?? props.placeholder,
      buttonRef,
      inputRef,
      listBoxRef,
      popoverRef,
    },
    state,
  );

  // Input focus
  const { isFocusVisible, isFocused, focusProps } = useFocusRing({ isTextInput: true, autoFocus: props.autoFocus });

  const stackRef = useRef<HTMLDivElement>(null);
  const stackRect = useResizeObserver(stackRef);
  const extraActionRef = useRef<HTMLDivElement>(null);
  const extraActionRect = useResizeObserver(extraActionRef);
  const listBoxWidth = useMemo(() => {
    let w = stackRect?.width ?? props.width;

    if (is(Number, w) && extraActionRect != null) {
      w = w - extraActionRect.width - 3; // 3 => column-gap
    }

    return w;
  }, [stackRect, extraActionRect, props.width]);

  return (
    <>
      <div style={{ minWidth: props.width, flex: 1 }} ref={stackRef} data-testid="combobox">
        <div className={formStyle.input_layout}>
          {props.label != undefined && (
            <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
              <Label {...labelProps}>{props.label}</Label>
              <Spacer />
              {!!props.isReadOnly && (
                <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />
              )}
              {!props.isRequired && !props.isReadOnly && (
                <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
              )}
            </HStack>
          )}
          <Description
            {...descriptionProps}
            data-testid="combobox-description"
            className={clsx(formStyle.input_layout_column1, {
              [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
            })}
          >
            {props.description}
          </Description>
          <HStack
            spacing={3}
            className={clsx(style.input_group, formStyle.input_layout_column1, {
              [style.disabled]: props.isDisabled,
              [style.focus]: isFocused || isFocusVisible,
              [style.error]: props.isInvalid,
            })}
            style={props.style}
            ref={boxRef}
          >
            {!props.label && (
              <VisuallyHidden>
                <label {...labelProps}>{props.placeholder}</label>
              </VisuallyHidden>
            )}
            {!!props.icon && <Icon icon={props.icon} className={style.icon} />}
            <input {...mergeProps(inputProps, focusProps)} className={style.input} ref={inputRef} />
            <div className={style.action}>
              {!!props.isLoading && <LoadingSpinner size={16} style={{ marginRight: 3 }} />}
              {!!props.action && !props.isDisabled && props.action}
              <TriggerButton
                {...buttonProps}
                icon={"unfold_more"}
                label={""}
                onPress={(e) => {
                  // Force-focus at the input
                  inputRef.current?.focus();
                  buttonProps.onPress?.(e);
                }}
                ref={buttonRef}
                isDisabled={props.isDisabled}
              />
            </div>
          </HStack>
          {props.extraAction != undefined && (
            <HStack className={formStyle.input_layout_column2} ref={extraActionRef}>
              {props.extraAction}
            </HStack>
          )}

          {props.errorMessage != undefined && !!props.isInvalid && (
            <div
              role="alert"
              {...errorMessageProps}
              className={clsx(formStyle.error_message, formStyle.input_layout_column1)}
              data-testid="number-field-error"
            >
              {props.errorMessage as ReactNode}
            </div>
          )}
        </div>
      </div>
      <Popover ref={popoverRef} state={state} triggerRef={boxRef} offset={5} placement="bottom">
        <ListBox
          {...listBoxProps}
          listBoxRef={listBoxRef}
          state={state}
          className={styleMenu.menu}
          style={{ minWidth: listBoxWidth }}
        />
      </Popover>
    </>
  );
};

export const ComboBox = forwardRef(ComboBoxRender);
